import React from 'react';
import { Typography, Box } from '@mui/material';

/**
 * TechnicalScoringRubric
 *
 * Drop this component into any page wrapped by your MUI <ThemeProvider>.
 * It pulls colors from your CSS variables (--color-primary, --color-text, etc.)
 * and typography from the MUI theme, so it automatically respects light/dark mode.
 *
 * Usage:  <TechnicalScoringRubric />
 */

const criteria = [
  {
    name: 'Focus',
    description: 'Mind-body connection; commitment to the moment',
    levels: [
      'Performer appears distracted or disconnected; breaks character frequently',
      'Focus is inconsistent; occasional lapses in concentration or character',
      'Sustained focus with only minor, brief lapses; generally committed',
      'Fully absorbed in performance from start to finish; complete presence',
    ],
  },
  {
    name: 'Confidence',
    description: 'Stage presence; command of technical skills',
    levels: [
      'Hesitant or uncertain; relies on watching others for cues',
      'Occasionally confident; some visible uncertainty in execution',
      'Generally assured; performs most skills with visible confidence',
      'Commands the stage; executes skills with authority and assurance',
    ],
  },
  {
    name: 'Co-operation',
    description: 'Group unity; serves the collective performance',
    levels: [
      'Noticeably out of sync; draws attention away from the group',
      'Sometimes unified; inconsistent awareness of group timing or spacing',
      'Works well within the group; minor lapses in synchronization',
      'Seamless group cohesion; enhances the ensemble without overshadowing',
    ],
  },
  {
    name: 'Projection',
    description: 'Performer–audience connection; putting the audience at ease',
    levels: [
      'Little connection with audience; energy does not reach beyond the stage',
      'Some moments of connection; projection is uneven or sporadic',
      'Engages audience consistently; energy and intention carry well',
      'Captivates the audience; performance fills the space effortlessly',
    ],
  },
  {
    name: 'Technique',
    description: 'Execution of movement skills; safe practice',
    levels: [
      'Frequent errors in form; limited control; safety concerns evident',
      'Basic skills present but inconsistent; some control issues',
      'Solid execution with minor errors; demonstrates control and safety',
      'Clean, precise execution; strong control with safe, intentional movement',
    ],
  },
  {
    name: 'Dynamics',
    description: 'Variation in force, speed, and size of movement',
    levels: [
      'Movement is flat; little variation in quality, speed, or force',
      'Some variation attempted but transitions between qualities are abrupt or unclear',
      'Good range of dynamics; transitions are mostly smooth and intentional',
      'Rich, nuanced dynamics; seamless modulation of force, speed, and scale',
    ],
  },
  {
    name: 'Endurance',
    description: 'Sustained energy throughout the performance',
    levels: [
      'Energy drops noticeably; visible fatigue significantly affects quality',
      'Energy wanes in sections; some recovery but inconsistent stamina',
      'Maintains energy through most of the piece; minor dips near the end',
      'Consistent, high-level energy from opening to final moment',
    ],
  },
  {
    name: 'Expression',
    description: 'Emotional depth; feeling conveyed through movement',
    levels: [
      'Flat or absent expression; movement feels mechanical',
      'Some emotion shown but not sustained or connected to the movement',
      'Emotion is clear and mostly connected to movement intent',
      'Deeply expressive; movement and emotion are inseparable and compelling',
    ],
  },
];

const levelHeaders = [
  { num: '1', label: 'Limited' },
  { num: '2', label: 'Moderate' },
  { num: '3', label: 'Considerable' },
  { num: '4', label: 'Excellent' },
];

const levelColors = ['#d94f4f', '#d4903a', '#3a8fb7', '#2e7d5e'];

const styles = {
  container: {
    width: '100%',
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '24px',
    backgroundColor: 'var(--color-background)',
    color: 'var(--color-text)',
  },
  header: {
    textAlign: 'center',
    paddingBottom: '12px',
    borderBottom: '3px solid var(--color-primary)',
    marginBottom: '16px',
  },
  scoreKey: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  keyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  dot: (color) => ({
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: '11px',
    backgroundColor: color,
  }),
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '1rem',
  },
  th: {
    backgroundColor: 'var(--color-bg-header)',
    color: 'var(--color-background)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontSize: '0.8rem',
    padding: '10px 8px',
    textAlign: 'center',
    border: '1px solid var(--color-bg-header)',
  },
  thFirst: {
    textAlign: 'left',
    width: '18%',
  },
  thLevel: {
    width: '20.5%',
  },
  td: {
    padding: '8px',
    verticalAlign: 'top',
    borderRight: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'rgba(128,128,128,0.25)',
    lineHeight: 1.35,
  },
  tdFirst: {
    borderLeft: '1px solid',
    borderColor: 'rgba(128,128,128,0.25)',
  },
  criteriaName: {
    fontWeight: 700,
    marginBottom: '2px',
    color: 'var(--color-primary)',
  },
  criteriaDesc: {
    fontSize: '0.8rem',
    opacity: 0.6,
    fontStyle: 'italic',
    lineHeight: 1.25,
  },
  levelCell: {},
};

const TechnicalScoringRubric = () => {
  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Box sx={styles.header}>
        <Typography variant="title" component="h1" sx={{ color: 'var(--color-primary)' }}>
          Technical Scoring Rubric
        </Typography>
        <Typography
          variant="h3"
          component="p"
          sx={{ fontSize: '13px', letterSpacing: '3px', opacity: 0.6, textTransform: 'uppercase', fontStyle: 'normal' }}
        >
          Ontario Secondary School Dancefest
        </Typography>
      </Box>

      {/* Score Key */}
      <Box sx={styles.scoreKey}>
        {levelHeaders.map((lh, i) => (
          <Box key={lh.num} sx={styles.keyItem}>
            <span style={styles.dot(levelColors[i])}>{lh.num}</span>
            {lh.label}
          </Box>
        ))}
      </Box>

      {/* Rubric Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, ...styles.thFirst }}>Criterion</th>
            {levelHeaders.map((lh) => (
              <th key={lh.num} style={{ ...styles.th, ...styles.thLevel }}>
                {lh.num} — {lh.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {criteria.map((c, rowIndex) => (
            <tr
              key={c.name}
              style={rowIndex % 2 === 1 ? { backgroundColor: 'rgba(128,128,128,0.05)' } : {}}
            >
              <td style={{ ...styles.td, ...styles.tdFirst }}>
                <Typography variant="h2" component="div" sx={{ ...styles.criteriaName, fontSize: '1.1rem' }}>
                  {c.name}
                </Typography>
                <div style={styles.criteriaDesc}>{c.description}</div>
              </td>
              {c.levels.map((level, li) => (
                <td key={li} style={{ ...styles.td, ...styles.levelCell }}>
                  {level}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

export default TechnicalScoringRubric;
